"use client";

import { apiClient } from "./api";

export interface NotificationStatus {
  enabled: boolean;
  message?: string;
}

export interface PushTokenInfo {
  id: string;
  playerId: string;
  platform: "ios" | "android" | "web";
  deviceName?: string;
  appVersion?: string;
}

// =============================================================================
// In-App Notification Types
// =============================================================================

export type NotificationStyle = "toast" | "bell_open" | "bell_badge";

export type NotificationType = "alarm" | "notification" | "system" | "announcement";

/**
 * InAppNotification - represents a notification with joined alarm data
 * Maps to NotificationWithAlarm from the backend
 */
export interface InAppNotification {
  // Core notification fields
  id: string;
  user_id: string;
  notification_type: NotificationType;
  notification_style?: NotificationStyle;
  message?: string;
  metadata?: Record<string, unknown>;
  read_at?: string;
  dismissed_at?: string;
  created_at: string;

  // Joined alarm fields (present when notification_type === "alarm")
  alarm_id?: string;
  device_id?: string;
  alarm_type?: string;
  severity?: "critical" | "major" | "minor" | "warning" | "info";
  status?: "active" | "acknowledged" | "cleared";
  display_name?: string;
  alarm_message?: string;
  device_name?: string;

  // Computed/derived field for backwards compatibility
  title?: string;
}

export interface NotificationListResponse {
  notifications: InAppNotification[];
  total: number;
  unread_count: number;
  page: number;
}

export interface UnreadCountResponse {
  count: number;
}

// =============================================================================
// Alert Subscription Types
// =============================================================================

export interface AlertSubscription {
  id: string;
  user_id: string;
  organization_id?: string;
  site_id?: string;
  device_profile_id?: string;
  device_id?: string;
  alarm_type?: string;
  min_severity: string;
  notification_style: NotificationStyle;
  is_role_based: boolean;
  created_at: string;
  updated_at: string;
  // Enriched fields from GET with details
  organization_name?: string;
  site_name?: string;
  device_profile_name?: string;
  device_name?: string;
}

export interface CreateSubscriptionRequest {
  organization_id?: string;
  site_id?: string;
  device_profile_id?: string;
  device_id?: string;
  alarm_type?: string;
  min_severity: string;
  notification_style?: NotificationStyle;
}

export interface UpdateSubscriptionRequest {
  alarm_type?: string;
  min_severity?: string;
  notification_style?: NotificationStyle;
}

// =============================================================================
// Notification Preferences Types
// =============================================================================

export interface NotificationPreferences {
  push_enabled: boolean;
  in_app_enabled: boolean;
  email_enabled: boolean;
  severity_critical: boolean;
  severity_major: boolean;
  severity_minor: boolean;
  severity_warning: boolean;
  severity_info: boolean;
  quiet_hours_enabled: boolean;
  quiet_hours_start?: string;
  quiet_hours_end?: string;
  quiet_hours_override_critical: boolean;
}

// =============================================================================
// Snooze Types
// =============================================================================

export interface AlarmSnooze {
  id: string;
  user_id: string;
  alarm_id: string;
  snoozed_until: string;
  snooze_reason?: string;
  created_at: string;
}

export interface SnoozeAlarmRequest {
  duration_minutes: number;
  reason?: string;
}

/**
 * Push Notifications API - for managing push notification tokens and testing
 */
export const pushNotificationsApi = {
  /**
   * Get notification service status
   */
  getStatus: async (): Promise<NotificationStatus> => {
    const response = await apiClient.get<NotificationStatus>("/v1/notifications/status");
    return response.data;
  },

  /**
   * Get all registered push tokens for the current user
   */
  getTokens: async (): Promise<PushTokenInfo[]> => {
    const response = await apiClient.get<PushTokenInfo[]>("/v1/notifications/tokens");
    return response.data;
  },

  /**
   * Send a test notification to the current user
   */
  sendTest: async (): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>("/v1/notifications/test");
    return response.data;
  },

  /**
   * Send a test notification to a specific user (admin only)
   */
  sendTestToUser: async (userId: string): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>(`/v1/admin/notifications/test/${userId}`);
    return response.data;
  },
};

// Keep legacy export for backwards compatibility
export const notificationsApi = pushNotificationsApi;

// =============================================================================
// In-App Notifications API
// =============================================================================

export const inAppNotificationsApi = {
  /**
   * Get paginated list of notifications
   */
  getNotifications: async (params?: {
    unread?: boolean;
    severity?: string;
    page?: number;
    page_size?: number;
  }): Promise<NotificationListResponse> => {
    const response = await apiClient.get<NotificationListResponse>("/v1/notifications", {
      params,
    });
    return response.data;
  },

  /**
   * Get unread notification count
   */
  getUnreadCount: async (): Promise<number> => {
    const response = await apiClient.get<UnreadCountResponse>("/v1/notifications/unread-count");
    return response.data.count;
  },

  /**
   * Get a single notification
   */
  getNotification: async (id: string): Promise<InAppNotification> => {
    const response = await apiClient.get<{ notification: InAppNotification }>(`/v1/notifications/${id}`);
    return response.data.notification;
  },

  /**
   * Mark a notification as read
   */
  markAsRead: async (id: string): Promise<void> => {
    await apiClient.put(`/v1/notifications/${id}/read`);
  },

  /**
   * Mark all notifications as read
   */
  markAllAsRead: async (): Promise<{ marked: number }> => {
    const response = await apiClient.put<{ success: boolean; marked: number }>("/v1/notifications/mark-all-read");
    return { marked: response.data.marked };
  },

  /**
   * Dismiss (delete) a notification
   */
  dismiss: async (id: string): Promise<void> => {
    await apiClient.delete(`/v1/notifications/${id}`);
  },
};

// =============================================================================
// Alert Subscriptions API
// =============================================================================

export const alertSubscriptionsApi = {
  /**
   * Get all alert subscriptions for the current user
   */
  getSubscriptions: async (): Promise<AlertSubscription[]> => {
    const response = await apiClient.get<{ subscriptions: AlertSubscription[] }>("/v1/users/me/alert-subscriptions");
    return response.data.subscriptions;
  },

  /**
   * Create a new alert subscription
   */
  createSubscription: async (data: CreateSubscriptionRequest): Promise<AlertSubscription> => {
    const response = await apiClient.post<{ subscription: AlertSubscription }>("/v1/users/me/alert-subscriptions", data);
    return response.data.subscription;
  },

  /**
   * Update an alert subscription
   */
  updateSubscription: async (id: string, data: UpdateSubscriptionRequest): Promise<AlertSubscription> => {
    const response = await apiClient.put<{ subscription: AlertSubscription }>(`/v1/users/me/alert-subscriptions/${id}`, data);
    return response.data.subscription;
  },

  /**
   * Delete an alert subscription
   */
  deleteSubscription: async (id: string): Promise<void> => {
    await apiClient.delete(`/v1/users/me/alert-subscriptions/${id}`);
  },
};

// =============================================================================
// Notification Preferences API
// =============================================================================

export const notificationPreferencesApi = {
  /**
   * Get notification preferences
   */
  getPreferences: async (): Promise<NotificationPreferences> => {
    const response = await apiClient.get<{ preferences: NotificationPreferences }>("/v1/users/me/notification-preferences");
    return response.data.preferences;
  },

  /**
   * Update notification preferences
   */
  updatePreferences: async (prefs: Partial<NotificationPreferences>): Promise<NotificationPreferences> => {
    const response = await apiClient.put<{ preferences: NotificationPreferences }>("/v1/users/me/notification-preferences", prefs);
    return response.data.preferences;
  },
};

// =============================================================================
// Alarm Snooze API
// =============================================================================

export const alarmSnoozeApi = {
  /**
   * Snooze an alarm
   */
  snooze: async (alarmId: string, data: SnoozeAlarmRequest): Promise<AlarmSnooze> => {
    const response = await apiClient.post<{ snooze: AlarmSnooze }>(`/v1/alarms/${alarmId}/snooze`, data);
    return response.data.snooze;
  },

  /**
   * Get snooze status for an alarm
   */
  getSnoozeStatus: async (alarmId: string): Promise<{ snoozed: boolean; snooze?: AlarmSnooze }> => {
    const response = await apiClient.get<{ snoozed: boolean; snooze?: AlarmSnooze }>(`/v1/alarms/${alarmId}/snooze`);
    return response.data;
  },

  /**
   * Remove snooze from an alarm
   */
  unsnooze: async (alarmId: string): Promise<void> => {
    await apiClient.delete(`/v1/alarms/${alarmId}/snooze`);
  },
};
